import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import Link from "@fuse/core/Link";
import FuseSvgIcon from "@fuse/core/FuseSvgIcon";
import { darken } from "@mui/material/styles";
import { alpha } from "@mui/system/colorManipulator";
import Tooltip from "@mui/material/Tooltip";
import clsx from "clsx";
import Popover, { PopoverProps } from "@mui/material/Popover/Popover";
import useUser from "@auth/useUser";
import { useNavigate } from "react-router-dom";
import { getPlan } from "@/utils/localStorage";
import { useMemo } from "react";
import { useLogoutMutation } from "@/store/services/auth";

type UserMenuProps = {
  className?: string;
  popoverProps?: Partial<PopoverProps>;
  arrowIcon?: string;
};

/**
 * The user menu.
 */
function UserMenu(props: UserMenuProps) {
  const {
    className,
    popoverProps,
    arrowIcon = "heroicons-outline:chevron-up",
  } = props;
  const navigate = useNavigate();
  const { data: user, isGuest, signOut: clearLocalAuth } = useUser();
  const [logout, { isLoading: isLogoutLoading }] = useLogoutMutation();
  const [userMenu, setUserMenu] = useState<HTMLElement | null>(null);
  const [showSignOutDialog, setShowSignOutDialog] = useState(false);
  const userMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenu(event.currentTarget);
  };
  const userMenuClose = () => {
    setUserMenu(null);
  };

  // Check if Applicant has a plan
  const hasPlan = useMemo(() => {
    if (user?.role !== "Applicant") {
      return true; // For non-applicants, plan doesn't matter
    }

    const plan = getPlan(); // getPlan() already returns parsed object or null

    // Check if plan is empty: null, empty array, or empty object {}
    const isNoPlan =
      !plan ||
      (Array.isArray(plan) && plan.length === 0) ||
      (typeof plan === "object" &&
        plan !== null &&
        Object.keys(plan).length === 0);

    return !isNoPlan;
  }, [user?.role]);

  // Hide "My Profile" for Applicants without a plan
  const showMyProfile = !(user?.role === "Applicant" && !hasPlan);

  const handleSignOutConfirm = async () => {
    try {
      await logout(undefined).unwrap();
    } catch (error) {
      console.error("Logout API call failed", error);
    } finally {
      await clearLocalAuth();
      navigate("/sign-in");
    }
  };

  if (!user) {
    return null;
  }

  return (
    <>
      <Button
        className={clsx(
          "user-menu flex justify-start shrink-0 min-h-14 h-14 rounded-lg p-2 space-x-3",
          className,
        )}
        sx={(theme) => ({
          borderColor: theme.palette.divider,
          "&:hover, &:focus": {
            backgroundColor: alpha(theme.palette.divider, 0.6),
            ...theme.applyStyles("dark", {
              backgroundColor: alpha(theme.palette.divider, 0.1),
            }),
          },
        })}
        onClick={userMenuClick}
        color="inherit"
      >
        {user?.photoURL ? (
          <Avatar
            sx={(theme) => ({
              background: theme.palette.background.default,
              color: theme.palette.text.secondary,
            })}
            className="avatar w-10 h-10 rounded-lg"
            alt="user photo"
            src={user?.photoURL}
            variant="rounded"
          />
        ) : (
          <Avatar
            sx={(theme) => ({
              background: (theme) =>
                darken(theme.palette.background.default, 0.05),
              color: theme.palette.text.secondary,
            })}
            className="avatar md:mx-1"
          >
            {user?.displayName?.[0]}
          </Avatar>
        )}
        <div className="flex flex-col">
          <Typography
            className="subtitle flex text-md font-medium tracking-tighter leading-none break-all text-left"
            color="text.secondary"
          >
            {user?.email}
          </Typography>
        </div>
        <div className="flex shrink-0 items-center space-x-2 mr-0">
          <FuseSvgIcon className="arrow" size={13}>
            {arrowIcon}
          </FuseSvgIcon>
        </div>
      </Button>
      <Popover
        open={Boolean(userMenu)}
        anchorEl={userMenu}
        onClose={userMenuClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        classes={{
          paper: "py-2 min-w-64",
        }}
        {...popoverProps}
      >
        {isGuest ? (
          <>
            <MenuItem component={Link} to="/sign-in" role="button">
              <ListItemIcon className="min-w-9">
                <FuseSvgIcon>heroicons-outline:lock-closed</FuseSvgIcon>
              </ListItemIcon>
              <ListItemText primary="Sign In" />
            </MenuItem>
          </>
        ) : (
          <>
            {showMyProfile && (
              <MenuItem
                component={Link}
                to="/apps/settings/account"
                onClick={userMenuClose}
                role="button"
              >
                <ListItemIcon className="min-w-9">
                  <FuseSvgIcon>heroicons-outline:user-circle</FuseSvgIcon>
                </ListItemIcon>
                <ListItemText primary="My Profile" />
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                userMenuClose();
                setShowSignOutDialog(true);
              }}
            >
              <ListItemIcon className="min-w-9">
                <FuseSvgIcon>
                  heroicons-outline:arrow-right-on-rectangle
                </FuseSvgIcon>
              </ListItemIcon>
              <ListItemText primary="Sign out" />
            </MenuItem>
          </>
        )}
      </Popover>

      {/* Logout Confirmation Dialog */}
      <Dialog
        open={showSignOutDialog}
        onClose={() => {
          if (!isLogoutLoading) {
            setShowSignOutDialog(false);
          }
        }}
        aria-labelledby="signout-confirmation-title"
        PaperProps={{
          sx: {
            backgroundColor: "#fff",
            color: "common.black",
            width: "100%",
            maxWidth: "300px",
            '& .MuiDialogTitle-root': {
              padding: "16px 24px 5px 24px",
            },
          },
        }}
      >
        <DialogTitle id="signout-confirmation-title">Sign out</DialogTitle>
        <DialogContent>
          <DialogContentText color="common.black">
            Are you sure you want to sign out?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 1.5, mt: 1, backgroundColor: "#f1f1f1" }}>
          <Button
            color="secondary"
            variant="outlined"
            size="small"
            disabled={isLogoutLoading}
            onClick={() => setShowSignOutDialog(false)}
            sx={{ '&:disabled': { borderColor: "common.black", color: 'common.black', opacity: 0.35 } }}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={isLogoutLoading}
            size="small"
            onClick={async () => {
              await handleSignOutConfirm();
            }}
            startIcon={
              isLogoutLoading ? <CircularProgress size={16} color="inherit" /> : null
            }
            sx={{ '&:disabled': { backgroundColor: "error.main", color: 'error.contrastText', opacity: 0.5 } }}
          >
            Sign out
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default UserMenu;
